import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FieldValues } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import localForage from 'localforage';
import { useQuery } from '@tanstack/react-query';

import usePushDataLayerEvent from '../../../../utils/google-analytics.util';
import { minutesToHours } from '../../../../utils/datetime.util';
import { setProvider } from '../../../../store/slices/generalSlice';
import { RootState } from '../../../../store/rootReducer';
import useManageStorage from '../../../../hooks/useManageStorage/useManageStorage';
import { theme } from '../../../../global-styles/colors';
import { Heading2Text } from '../../../../components/typography/typography';
import ModalContainer from '../../../../components/common/modal-container/modal-container';
import CardInfo from '../../../../components/card-info/CardInfo';
import AlxButton from '../../../../components/alx-components/button';
import { images } from '../../../../assets/images/images';
import { getAssistanceInformation } from '../../../../api/assistance/assistance';
import { ConfirmationSuccessStyle } from './confirmation-success-style';

const ConfirmationSuccess = () => {
  const [renderModal, setRenderModal] = useState<string>('');
  const { t } = useTranslation(['common', 'auth', 'aia']);
  const navigate = useNavigate();

  const { intervention, identifier, entityCode } = useParams<{
    state: string;
    intervention: string;
    identifier: string;
    entityCode?: string;
  }>();
  const currentEntity = useSelector((state: RootState) => state.general.provider);
  const dispatch = useDispatch();
  const { writeToStorage } = useManageStorage();
  const { pushDataLayerEvent } = usePushDataLayerEvent();

  const { data } = useQuery({
    queryKey: ['interventionDataQuery', {}],
    queryFn: async () => {
      const data: FieldValues | null = await localForage.getItem('queries');
      return data;
    },
    refetchOnWindowFocus: false,
  });

  const dataLength = Object.keys(data || {}).length;
  const { data: assistanceInformation } = useQuery({
    queryKey: ['getAssistanceInformation', { identifier, entityCode }],
    queryFn: () => getAssistanceInformation(identifier, entityCode, 'ConfirmationSuccess'),
    enabled: !!(!dataLength && identifier && entityCode),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const assistanceData = useMemo(() => {
    if (dataLength && data) {
      return {
        providence: data?.interventionData?.provinciaOrigenSiniestro,
        locality: data?.interventionData?.localidadOrigenSiniestro,
        file: data?.interventionData.numeroExpediente,
        time: minutesToHours(data?.interventionData.tiempoServicio),
      };
    } else if (assistanceInformation) {
      return {
        file: assistanceInformation.expediente,
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, assistanceInformation]);

  const redirect = () => {
    if (!assistanceInformation || !assistanceInformation?.expediente || !identifier) {
      navigate('/');
      return;
    } else {
      navigate(`/unregistered/${assistanceInformation?.intervencion}/${identifier}`);
    }
  };

  const onPostponeSurvey = async () => {
    setRenderModal('');
    await writeToStorage('postponedSurvey', { file: assistanceInformation?.expediente, intervention, identifier });
    redirect();
    pushDataLayerEvent('posponer_encuesta', null, 'finalizar-servicio');
    return;
  };

  // // TO DO: WE NEED TO THINK ABOUT THIS
  // if (!data) return <></>;

  const showOrigin = assistanceData?.providence && assistanceData?.locality;

  useEffect(() => {
    if (entityCode && currentEntity !== entityCode) {
      dispatch(setProvider({ provider: entityCode }));
    }
  }, [entityCode, currentEntity, dispatch]);

  useEffect(() => {
    if (assistanceData?.providence) {
      pushDataLayerEvent('servicio_finalizado', { tiempo_servicio: assistanceData?.providence }, 'finalizar-servicio');
    }
  }, [assistanceData?.providence, pushDataLayerEvent]);

  return (
    <>
      <ConfirmationSuccessStyle>
        <Heading2Text className="m-24px" as="h1" weight={600} color={theme.neutrals.white}>
          {t('aia:confirmation.success.title')}
        </Heading2Text>
        <div className="succes-info m-24px">
          <CardInfo
            data={[
              ...(showOrigin
                ? [
                    {
                      title: t('aia:confirmation.success.info.final-destination'),
                      description: [`${assistanceData?.providence} - ${assistanceData?.locality}`],
                      separator: true,
                    },
                  ]
                : []),
              {
                title: t('aia:confirmation.success.info.time-of-service'),
                description: [assistanceData?.time ?? '00:00'],
                separator: false,
              },
            ]}
          />
        </div>
        <AlxButton className="secondary m-24px" onClick={() => setRenderModal('quiz')}>
          {t('common:buttons.conclude')}
        </AlxButton>
        <div className="confirmation-img">
          <img src={images.cars} alt="" />
        </div>
      </ConfirmationSuccessStyle>
      {/* Modal Reset */}
      <ModalContainer
        buttons={[
          {
            className: 'primary',
            onClick: () => {
              if (!assistanceData?.file) return;
              pushDataLayerEvent('empezar_encuesta', null, 'finalizar-servicio');
              navigate(`/survey/${assistanceData?.file}/${intervention}`);
            },
            label: t('aia:confirmation.success.do-survey'),
          },
          {
            className: 'secondary',
            onClick: onPostponeSurvey,
            label: t('aia:confirmation.success.to-do-later'),
          },
        ]}
        active={renderModal === 'quiz'}
        onClose={() => setRenderModal('')}
        title={t('aia:confirmation.success.quiz-modal.title')}
        text={t('aia:confirmation.success.quiz-modal.text')}
        dataCy="reset-form-modal"
      />
    </>
  );
};

export default ConfirmationSuccess;
